import axios from "axios";
import { toast } from "sonner";
import * as offline from "./offline";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ---- Trabajo sin cobertura (Bloque 6): respuestas GET guardadas en el dispositivo y cola de revisiones/fotos ----
const baseAdapter = axios.getAdapter(axios.defaults.adapter);
const relUrl = (cfg) => (cfg.url || "").replace(API, "");
const isNetworkError = (err) => !err?.response && err?.code !== "ERR_CANCELED";
const okResponse = (cfg, data, extra = {}) => ({ data, status: 200, statusText: "OK (sin conexión)", headers: {}, config: cfg, request: null, ...extra });

async function offlineFallback(cfg, err) {
  const method = (cfg.method || "get").toLowerCase();
  const url = relUrl(cfg);
  if (method === "get" && offline.isCacheable(url)) {
    const hit = await offline.cacheGet(url);
    if (hit) return okResponse(cfg, hit.data, { offline: true, cachedAt: hit.at });
  }
  if (method === "post" && url === "/files/upload" && cfg.data instanceof FormData) {
    const f = cfg.data.get("file");
    if (f) {
      const id = `offline-file-${crypto.randomUUID()}`;
      await offline.fileAdd(id, f, f.name || "foto.jpg");
      return okResponse(cfg, { id, filename: f.name || "foto.jpg", offline: true });
    }
  }
  if (method === "post" && url === "/inspections") {
    const payload = typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data;
    const op = { op_id: `op-${crypto.randomUUID()}`, type: "inspection", element_id: payload.element_id, session_id: payload.session_id || null,
                 client_at: new Date().toISOString(), payload, status: "pending" };
    await offline.queueAdd(op);
    // El elemento pasa a «revisado» en la copia local de la revisión general activa para que desaparezca de pendientes.
    const keys = Object.keys(localStorage).filter((k) => k.startsWith("gmao:offline-active:"));
    for (const k of keys) {
      const u = localStorage.getItem(k);
      await offline.cachePatch(u, (s) => (s && (!op.session_id || s.id === op.session_id) ? { ...s, revised_element_ids: [...new Set([...(s.revised_element_ids || []), payload.element_id])] } : s));
    }
    offline.notifyOfflineChange();
    return okResponse(cfg, { id: op.op_id, queued: true, offline: true, ...payload });
  }
  throw err;
}

api.defaults.adapter = async (cfg) => {
  if (offline.isOffline()) return offlineFallback(cfg, Object.assign(new Error("Sin conexión"), { code: "ERR_NETWORK", config: cfg }));
  try {
    const r = await baseAdapter(cfg);
    const url = relUrl(cfg);
    if ((cfg.method || "get").toLowerCase() === "get" && offline.isCacheable(url)) {
      let data = r.data;
      if (typeof data === "string") { try { data = JSON.parse(data); } catch { data = null; } }
      if (data !== null) offline.cacheSet(url, data);
      if (url.startsWith("/revision-sessions/active?")) localStorage.setItem(`gmao:offline-active:${url}`, url);
    }
    return r;
  } catch (err) {
    if (isNetworkError(err)) return offlineFallback(cfg, err);
    throw err;
  }
};

// Acceso directo por URL a un centro/empresa/elemento no autorizado: aviso y vuelta a la pantalla inicial.
let _deniedNoticeAt = 0;
api.interceptors.response.use((r) => r, (err) => {
  const st = err?.response?.status;
  const detail = String(err?.response?.data?.detail || "");
  const isGet = (err?.config?.method || "").toLowerCase() === "get";
  if (st === 403 && isGet && detail.startsWith("No tienes acceso") && !window.location.pathname.startsWith("/login")) {
    if (Date.now() - _deniedNoticeAt > 2000) {
      _deniedNoticeAt = Date.now();
      toast.error(detail);
      setTimeout(() => window.location.replace("/"), 900);
    }
    return new Promise(() => {}); // la página se recarga; se evita una promesa rechazada sin gestionar
  }
  return Promise.reject(err);
});

export function fileUrl(fileId) {
  if (!fileId) return null;
  return `${API}/files/${fileId}`;
}

// Miniatura optimizada (WebP redimensionada en servidor) para listados y galerías.
export function thumbUrl(fileId, w = 80) {
  if (!fileId) return null;
  return `${API}/files/${fileId}?w=${w}`;
}

// Devuelve un enlace de navegación a partir de un enlace pegado, coordenadas "lat, lng",
// una dirección o los campos gps_lat/gps_lng. null si no hay datos.
export function mapsHref(inst) {
  if (!inst) return null;
  const raw = (inst.ubicacion_maps || "").trim();
  if (raw) {
    if (/^https?:\/\//i.test(raw)) return raw; // ya es un enlace
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(raw)}`;
  }
  if (inst.gps_lat != null && inst.gps_lng != null) {
    return `https://www.google.com/maps/search/?api=1&query=${inst.gps_lat},${inst.gps_lng}`;
  }
  if (inst.direccion) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(inst.direccion)}`;
  }
  return null;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const ELEMENT_TYPES = {
  seccional: { label: "Puertas seccionales", color: "#DC2626" },
  muelle: { label: "Muelles de carga hidráulicos", color: "#2563EB" },
  rapida: { label: "Puertas rápidas", color: "#16A34A" },
  rf_rapida: { label: "Puertas RF rápidas", color: "#0891B2" },
  rf_guillotina: { label: "RF de guillotina", color: "#DB2777" },
  abrigo: { label: "Abrigos de muelle", color: "#64748B" },
  rf_persona: { label: "RF de paso de persona", color: "#0D9488" },
  cortafuegos: { label: "Puertas cortafuegos", color: "#EA580C" },
  retenedor: { label: "Retenedores", color: "#7C3AED" },
  otro: { label: "Otro elemento", color: "#EAB308" },
};

// Tipos seleccionables en alta/edición de elemento (orden y etiquetas según requisito).
export const ELEMENT_TYPE_OPTIONS = [
  ["seccional", "Puerta seccional"],
  ["muelle", "Muelle de carga hidráulico"],
  ["rapida", "Puerta rápida"],
  ["rf_rapida", "RF Puerta rápida"],
  ["rf_guillotina", "RF de guillotina"],
  ["rf_persona", "RF Paso personal"],
  ["abrigo", "Abrigo de muelle"],
  ["retenedor", "Retenedor"],
  ["otro", "Otros elementos"],
];

export const WO_STATUS = {
  pendiente: { label: "Pendiente", color: "#6B7680" },
  asignada: { label: "Asignada", color: "#2F3D4C" },
  en_curso: { label: "En curso", color: "#4A6A86" },
  cerrada: { label: "Cerrada", color: "#2E7D5B" },
  facturada: { label: "Facturada", color: "#202830" },
  cancelada: { label: "Cancelada", color: "#64748B" },
};

// Fases del flujo de incidencia (independientes de abierta/cerrada/anulada).
export const INCIDENT_FASES = [
  ["pendiente", "Pendientes", "#C40016", "Sin empresa reparadora asignada", "Pendiente"],
  ["asignada", "Asignadas", "#2F3D4C", "Con empresa asignada, reparación no completada", "Asignada"],
  ["reparada", "Reparadas · pdte. factura", "#6E8F5E", "Reparación realizada, falta la factura del reparador", "Reparada · pdte. factura"],
  ["pendiente_facturar", "Pendientes de facturar", "#5B6670", "Documentación del reparador recibida; falta nuestra oferta", "Pendiente de facturar"],
  ["ofertada", "Ofertadas · pdte. cierre", "#4A6A86", "Oferta emitida, cierre no confirmado", "Ofertada · pdte. cierre"],
  ["cerrada", "Cerradas", "#2E7D5B", "Proceso cerrado", "Cerrada"],
];
export const FASE_META = Object.fromEntries(INCIDENT_FASES.map(([k, plural, color, desc, label]) => [k, { label, plural, color, desc }]));
export const faseOf = (i) => (i?.estado === "cerrada" ? "cerrada" : i?.fase || "pendiente");
export const ESTADO_META = { abierta: { label: "Abierta", color: "#C40016" }, cerrada: { label: "Cerrada", color: "#2E7D5B" }, anulada: { label: "Anulada", color: "#6B7680" } };
export const whatsappHref = (telefono, texto) => { const n = (telefono || "").replace(/[^\d+]/g, "").replace(/^\+/, "").replace(/^00/, ""); return n ? `https://wa.me/${n.length <= 9 ? "34" + n : n}?text=${encodeURIComponent(texto || "")}` : null; };

export const ROLE_LABELS = {
  admin: "Administrador",
  gestor: "Gestor FOR DECI",
  operator: "Operario",
  client_group: "Cliente (grupo)",
  client_plant: "Cliente (planta)",
};

export const URGENCY_COLORS = { urgente: "#C40016", alta: "#8B1D2A", media: "#2F3D4C", baja: "#6B7680" };
export function urgencyColor(u) {
  return URGENCY_COLORS[u] || "#94A3B8";
}

// Gravedad de un punto KO del checklist: los valores guardados siguen siendo baja/media/alta
// (compatibilidad con el histórico); solo cambian las etiquetas visibles.
// «Nivel de actuación» (campo gravedad): baja | media = requiere actuación o reparación | alta = cambiar componente.
export const GRAVEDAD_LABEL = { baja: "Baja", media: "Requiere actuación o reparación", alta: "Cambiar componente" };
export const GRAVEDAD_OPTIONS = ["baja", "media", "alta"];
export const gravedadLabel = (v) => GRAVEDAD_LABEL[v] || v || "—";
// Actuación de un punto KO valorable: reparar | sustituir (excluyentes). Registros antiguos: derivada de la gravedad.
export const ACCION_LABEL = { reparar: "Reparar", sustituir: "Sustituir" };
export const koAccion = (it) => {
  if (!it) return null;
  if (it.informativo && it.requiere_actuacion) return "reparar";
  if (it.informativo || it.informativa) return null;
  if (ACCION_LABEL[it.accion]) return it.accion;
  return it.gravedad === "alta" ? "sustituir" : it.gravedad === "media" ? "reparar" : null;
};
export const accionLabel = (it) => (it?.informativo && it?.requiere_actuacion ? "Reparar (solo MO)" : it?.informativo || it?.informativa ? "Informativa" : ACCION_LABEL[koAccion(it)] || "—");
// Identificación visible del elemento: matrícula o, si no tiene, su nº interno automático («elemento 025»).
export const matriculaLabel = (el) => (el?.matricula_interna ? el.matricula_interna : el?.seq != null ? `elemento ${String(el.seq).padStart(3, "0")}` : "Sin matrícula");
// Urgencia: campo independiente del nivel de actuación (por defecto media).
export const URGENCIA_OPTIONS = [["baja", "Baja"], ["media", "Media"], ["alta", "Alta"], ["urgente", "Urgente"]];
export const MEDIOS_AUXILIARES = [["carretilla", "Carretilla"], ["plataforma", "Plataforma elevadora"], ["brazo", "Brazo articulado"], ["grua", "Grúa"], ["transporte", "Transporte"], ["otros", "Otros"]];
// Entradas del checklist: string (punto antiguo), { h } (sección) o { id, p, valorable, opciones } (punto configurado).
export const isSection = (e) => e && typeof e === "object" && "h" in e;
export const pointText = (e) => (typeof e === "string" ? e : e && typeof e === "object" && "p" in e ? e.p : null);
export const pointConfig = (e) => (e && typeof e === "object" && "p" in e ? e : { p: e, valorable: true, opciones: [] });
// Comportamiento de un punto al marcar KO: informativa (comentario + foto) | mano_obra (solo trabajo) | recambio (Reparar / Sustituir).
export const COMPORTAMIENTO_LABEL = { informativa: "Observación informativa", mano_obra: "Actuación solo con mano de obra", recambio: "Actuación con posible recambio" };
export const pointBehavior = (cfg) => (cfg?.valorable === false ? "informativa" : cfg?.comportamiento === "mano_obra" ? "mano_obra" : "recambio");
// Clasificación del catálogo por el campo Observaciones («recambios» / «informativos»); el resto queda «sin clasificar» para revisión.
export const spareClass = (s) => { const o = (s?.observaciones || "").toString().toLowerCase(); if (o.includes("recambio")) return "recambio"; if (o.includes("informativ")) return "informativo"; return "sin_clasificar"; };
export const SPARE_CLASS_LABEL = { recambio: "Recambio", informativo: "Informativo", sin_clasificar: "Sin clasificar · revisar" };
export const esRecambio = (s) => spareClass(s) === "recambio";

// Origen de una incidencia agrupado para filtros: revisión / cliente (app, manual, operario) / otros existentes.
export const ORIGIN_LABEL = { revision: "Por revisión", cliente: "Del cliente", qr_publico: "Aviso QR público", excel: "Importadas (Excel)", oferta: "Desde oferta" };
export const originKey = (inc) => { const o = inc?.origin; if (o === "revision") return "revision"; if (o && ORIGIN_LABEL[o]) return o; return "cliente"; };

// Criterio ÚNICO para localizar la última revisión de un elemento: la más reciente por created_at.
export async function fetchLastInspection(elementId) {
  const r = await api.get(`/inspections?element_id=${elementId}`);
  const list = [...(r.data || [])].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  return list[0] || null;
}

export const CHECKLISTS = {
  seccional: [
    "Panel inferior", "Paneles intermedios", "Bisagras", "Sellado entre hojas", "Enclavamiento manual",
    "Dispositivo frente rotura cables", "Tirador puerta manual - Cuerda", "Cerradura", "Ventanas", "Cables",
    "Cables situados en rodillos lama inferior", "Protecciones (si las hay)",
    { h: "Sistema de guiado" },
    "Enclavamientos a obra", "Soportes ruedas laterales", "Ruedas laterales", "Soportes a techo", "Gomas",
    { h: "Puerta de paso" },
    "Maneta y cerradura", "Cierrapuertas", "Bisagras", "Gomas perimetrales", "Tapajuntas de seguridad", "Micro seguridad",
    { h: "Funcionamiento" },
    "Tensión de muelles", "Equilibrado muelles", "Estado muelles", "Soportes eje", "Estado eje",
    { h: "Partes eléctricas" },
    "Estado caja de maniobras", "Indicador mantenimiento", "Señales de advertencia", "Motor (enclavamiento y ruido)",
    "Fuerza motor", "Velocidad constante", "Final de carrera", "Fotocélula", "RadioBand", "Paro emergencia",
  ],
  rapida: [
    { h: "Mecánica · Guías laterales" },
    "Estado general", "Deformaciones", "Fijaciones",
    { h: "Mecánica · Lona o panel flexible" },
    "Desgarros", "Cortes", "Perforaciones", "Desgaste por roce", "Ventanas dañadas", "Tensado",
    { h: "Mecánica · Motor" },
    "Ruido", "Vibración",
    { h: "Seguridad · Fotocélulas" },
    "Alineación", "Funcionamiento (fotocélulas)", "Limpieza",
    { h: "Seguridad · Banda sensible" },
    "Activación correcta", "Tiempo de reacción",
    { h: "Seguridad · Paro de emergencia" },
    "Funcionamiento (paro de emergencia)", "Rearme",
    { h: "Seguridad · Detectores" },
    "Radar", "Lazo magnético", "Funcionamiento (detectores)",
    { h: "Seguridad · Junta intumescente" },
    "Estado", "Continuidad", "Desgaste",
    { h: "Funcionamiento · Apertura automática" },
    "Activación (apertura)", "Velocidad (apertura)",
    { h: "Funcionamiento · Cierre automático" },
    "Activación (cierre)", "Velocidad (cierre)",
  ],
  rf_rapida: ["Lona/cortina RF", "Guías laterales", "Motor", "Cuadro eléctrico", "Fotocélulas", "Sistema de cierre RF", "Fusible térmico", "Funcionamiento general"],
  rf_guillotina: [
    { h: "Hoja cortafuego" },
    "Inspección visual", "Abolladuras", "Daños en paneles",
    { h: "Alineación" },
    "Paralelismo respecto al hueco", "Posición respecto a guías", "Ausencia de roces",
    { h: "Carril superior" },
    "Estado del carril", "Suciedad", "Deformaciones (carril superior)", "Desgaste (carril superior)", "Soportes",
    { h: "Carros de suspensión" },
    "Desgaste (carros de suspensión)", "Holguras", "Rodamientos", "Ruidos", "Desplazamiento suave",
    { h: "Sistema de guiado inferior" },
    "Desgaste (guiado inferior)", "Fijación (guiado inferior)", "Obstrucciones", "Deformaciones (guiado inferior)",
    { h: "Contrapesos y cables · Cables" },
    "Hilos rotos", "Desgaste (cables)", "Tensado",
    { h: "Contrapesos y cables · Poleas" },
    "Desgaste (poleas)", "Giro libre", "Alineación (poleas)",
    { h: "Contrapesos y cables · Contrapesos" },
    "Integridad (contrapesos)", "Fijación (contrapesos)", "Libre movimiento",
    { h: "Sistema de retención · Electroimanes" },
    "Fijación (electroimanes)", "Alimentación", "Estado mecánico", "Prueba funcional", "Ausencia de bloqueos",
    { h: "Cierre automático" },
    "Liberación", "Inicio de movimiento", "Cierre total", "Tiempo de cierre correcto",
    { h: "Juntas cortafuego" },
    "Continuidad", "Integridad (juntas)", "Fijación (juntas)",
    { h: "Estanqueidad del cierre (puerta cerrada)" },
    "Ajuste lateral", "Ajuste superior", "Ajuste inferior",
  ],
  rf_persona: [
    { h: "Estado general de la hoja" },
    "Golpes", "Abolladuras", "Deformaciones", "Perforaciones", "Taladros realizados posteriormente",
    "Pasacables instalados incorrectamente", "Cerraduras sustituidas por modelos no homologados (invalida puerta)",
    "Mirillas añadidas (invalida puerta)",
    { h: "Marco y cerco" },
    "Deformaciones (marco y cerco)", "Fisuras", "Correcta unión con el tabique", "Ausencia de holguras excesivas",
    { h: "Bisagras" },
    "Holguras", "Desgaste", "Tornillos flojos", "Apertura suave",
    { h: "Cierre automático (cierrapuertas)" },
    "Fijaciones", "Fugas de aceite", "Brazo articulado", "Tornillería", "Cierre completo", "Cierre suave", "Ausencia de golpes excesivos",
    { h: "Cerradura y pestillo" },
    "Funcionamiento", "Alineación", "Cierre correcto", "Cerrar desde apertura total: el pestillo engancha correctamente",
    { h: "Solo en puertas de dos hojas" },
    "Secuencia correcta", "Comprobación: abrir ambas hojas y soltar",
    { h: "Retenedores electromagnéticos" },
    "Estado", "Fijación", "Alimentación", "Liberación inmediata", "Cierre automático",
    { h: "Juntas intumescentes" },
    "Continuidad", "Adherencia", "Ausencia de roturas", "Ausencia de pintura encima",
    { h: "Señalización" },
    "Señalización visible", "Cartelería obligatoria",
    { h: "Elementos prohibidos (RIESGO CRÍTICO)" },
    "Cuñas", "Cadenas", "Bridas", "Cierres improvisados", "Topes no autorizados",
  ],
  cortafuegos: ["Estado de hoja", "Guías", "Fusible térmico", "Muelle de cierre", "Funcionamiento general"],
  muelle: ["Limpieza", "Detección de tornillos", "Detección trozos metal sin identificar", "Detección partes rotas o limaduras metálicas", "Estado partes móviles", "Estado bisagras", "Estado juntas", "Enclavamientos metal-hormigón", "Tornillería fijación partes", "Soldaduras", "Linealidad vástago con cilindro hidráulico", "Existencia manchas aceite considerables", "Eliminación burbujas sistema hidráulico", "Presión tuercas latiguillos", "Nivel aceite", "Trapo limpio - comprobación pérdidas aceite", "Regulación sistema", "Bloqueo seguridad", "Paro emergencia"],
  abrigo: [
    { h: "Estructura portante" },
    "Deformaciones", "Golpes", "Estado de pintura", "Soldaduras", "Alineación", "Verticalidad", "Integridad estructural",
    { h: "Anclajes a fachada" },
    "Tornillos flojos", "Ausencia de fijaciones", "Corrosión", "Grietas en soporte",
    { h: "Lonas laterales" },
    "Cortes", "Rasgaduras", "Deshilachados", "Desgaste por fricción",
    { h: "Lona superior" },
    "Roturas", "Desgaste (lona superior)", "Deformaciones (lona superior)", "Desprendimientos", "Correcta cobertura del vehículo",
    { h: "Costuras y soldaduras textiles" },
    "Descosidos", "Despegues", "Costuras abiertas",
    { h: "Señalización" },
    "Correcta identificación visual",
    { h: "Bastidor retráctil" },
    "Funcionamiento", "Deformaciones (bastidor retráctil)", "Retorno automático",
    { h: "Limpieza" },
    "Buena",
  ],
  retenedor: ["Gancho", "Motor", "Sensores", "Señalización", "Funcionamiento general"],
  otro: ["Inspección visual", "Funcionamiento general"],
};

// Las entradas del checklist son strings (puntos de revisión) u objetos { h: "Título" } (cabeceras de sección).
const DEFAULT_CHECKLIST = ["Inspección visual", "Funcionamiento general"];

export function checklistPoints(tipo) {
  return (CHECKLISTS[tipo] || DEFAULT_CHECKLIST).map(pointText).filter((t) => t !== null);
}

// Construye los items de revisión asignando a cada punto su sección actual.
export const DELETE_ELEMENT_WARNING = (name) =>
  `¿Eliminar el elemento «${name}»?\n\nADVERTENCIA: se borrará de forma permanente su ficha, su posición en el plano y su etiqueta QR. Las revisiones, incidencias y órdenes históricas quedarán sin elemento asociado. Esta acción se registra en el historial de auditoría y no se puede deshacer.`;

export function buildChecklistItems(tipo) {
  const raw = CHECKLISTS[tipo] || DEFAULT_CHECKLIST;
  let seccion = "";
  const out = [];
  raw.forEach((e) => {
    if (isSection(e)) { seccion = e.h; return; }
    const c = pointConfig(e);
    out.push({ item: c.p, seccion, status: "ok", observaciones: "", gravedad: "baja", foto: null, punto_id: c.id || null, informativo: c.valorable === false, urgencia: "media",
      tipo_incidencia: null, actuaciones: [], componentes: [], pendiente_recambio: false, accion: null, solo_mano_obra: false, requiere_actuacion: false });
  });
  return out;
}

// Tipos de elemento configurables: se cargan desde /element-types y sustituyen (mutando en
// sitio) los objetos ELEMENT_TYPES / ELEMENT_TYPE_OPTIONS / CHECKLISTS que usa toda la app.
export function applyElementTypes(list) {
  if (!Array.isArray(list) || !list.length) return;
  Object.keys(ELEMENT_TYPES).forEach((k) => delete ELEMENT_TYPES[k]);
  Object.keys(CHECKLISTS).forEach((k) => delete CHECKLISTS[k]);
  ELEMENT_TYPE_OPTIONS.length = 0;
  list.forEach((t) => {
    ELEMENT_TYPES[t.key] = { label: t.label, color: t.color, active: t.active !== false, ficha: t.ficha_campos || [] };
    CHECKLISTS[t.key] = (t.checklist && t.checklist.length) ? t.checklist : [...DEFAULT_CHECKLIST];
    if (t.active !== false) ELEMENT_TYPE_OPTIONS.push([t.key, t.label]);  // los tipos desactivados conservan etiqueta/color pero no se ofrecen en altas
  });
}

export async function loadElementTypes() {
  try {
    const { data } = await api.get("/element-types");
    applyElementTypes(data);
    return data;
  } catch {
    return null;
  }
}

export const PROBLEMS = {
  door: ["No sube", "No baja", "Hace ruido", "Golpe en panel", "Cable destensado", "Motor no funciona", "Fotocélula falla", "Cuadro eléctrico con error"],
  muelle: ["No sube", "No baja", "Fuga hidráulica", "Labio no abre", "Golpe estructural", "Cilindro dañado", "Motor hidráulico falla"],
};

// Importes en formato español: 1.234,56 €
export const eur = (v) => `${Number(v || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

// Orden numérico de elementos (Muelle nº 1, 2, 3… 10, 11): usa `numero` o el último número de la denominación; sin número → al final (orden estable por nombre).
export const elementNumber = (el) => {
  if (el?.numero != null && el.numero !== "") return Number(el.numero);
  const m = [...String(el?.denominacion || "").matchAll(/(?<![A-Za-z0-9])(\d+)(?![A-Za-z0-9])/g)].map((x) => x[1]);
  return m.length ? Number(m[m.length - 1]) : null;
};
export const compareElements = (a, b) => {
  const na = elementNumber(a), nb = elementNumber(b);
  if (na != null && nb != null && na !== nb) return na - nb;
  if (na != null && nb == null) return -1;
  if (na == null && nb != null) return 1;
  return String(a?.denominacion || "").localeCompare(String(b?.denominacion || ""), "es", { numeric: true, sensitivity: "base" });
};
export const sortElements = (list) => [...(list || [])].sort(compareElements);

// Nombre en singular del tipo, para identificar elementos: «Muelle nº 2», «Puerta seccional nº 2»
export const ELEMENT_TYPE_SINGULAR = { seccional: "Puerta seccional", muelle: "Muelle", rapida: "Puerta rápida", rf_rapida: "Puerta RF rápida", rf_guillotina: "RF de guillotina", abrigo: "Abrigo de muelle", rf_persona: "RF de paso de persona", cortafuegos: "Puerta cortafuegos", retenedor: "Retenedor", otro: "Elemento" };
export const elementLabel = (el) => {
  if (!el) return "Sin elemento";
  const n = elementNumber(el);
  const tipo = ELEMENT_TYPE_SINGULAR[el.tipo] || ELEMENT_TYPES[el.tipo]?.label || el.tipo || "Elemento";
  return n != null ? `${tipo} nº ${n}` : `${tipo} · ${el.denominacion}`;
};
